import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DELIVERYTYPES, RequestsUpdatePickupDeliveryRequestDto } from '@impact/data';
import { firstValueFrom, Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { arrowRightIcon } from '../../../../../../../icons';
import { CheckoutFlowService } from '../../../../../../checkout/checkout-flow.service';
import { CheckoutService } from '../../../../../../checkout/checkout.service';
import { getDateObjFromDate, getTimeFromUTCDate, getUTCDateString } from '../../../../../../core/helpers';
import { FormService } from '../../../../../../form/form.service';

@Component({
    selector: 'app-checkout-step-delivery-tab-delivery',
    template: `
        <div *ngIf="form">
            <form class="flow__form" [formGroup]="form" (ngSubmit)="onSubmit()">
                <section class="flow__form-section">
                    <h6 class="text-small">{{ 'checkout.delivery_step____delivery_tab____delivery_address_form_label' | translate | uppercase }}</h6>
                    <app-input
                        class="user-info-input"
                        controlName="address"
                        [parentForm]="form"
                        [label]="'user_information.address' | translate"
                    ></app-input>

                    <div class="user-info-postal-number-city">
                        <app-input
                            class="user-info-input user-info-postal-number-city__postal-number"
                            controlName="zipCode"
                            [inputMode]="'decimal'"
                            [label]="
                                'user_information.postal_number' | translate
                            "
                            [parentForm]="form"
                        ></app-input>
                        <app-input
                            class="user-info-input user-info-postal-number-city__city"
                            controlName="city"
                            [parentForm]="form"
                            [label]="'user_information.city' | translate"
                        ></app-input>
                    </div>
                </section>

                <section class="flow__form-section">
                    <h6 class="text-small">{{ 'checkout.delivery_step____delivery_tab____delivery_date_form_label' | translate | uppercase }}</h6>
                    <app-datepicker [parentForm]="form" controlName="date" [label]="'Dato'" [daysToDelayMinDate]="6"></app-datepicker>
                </section>

                <section class="flow__form-section">
                    <h6 class="text-small">{{ 'checkout.delivery_step____delivery_tab____delivery_time_form_label' | translate | uppercase }}</h6>
                    <app-time-slot-picker
                        [currentSlot]="timeSlot"
                        (handleClick)="handleSlotClick($event)"
                        [hasErrors]="timeSlotInvalid"
                    ></app-time-slot-picker>
                </section>
                <div class="center grid-spacing-normal">
                    <app-button
                        *ngIf="!(checkoutFlowService.atFlowStart$ | async)"
                        buttonClass="button--tertiary button--has-spacing button--has-bottom-spacing"
                        [title]="'checkout.button____go_back_to_previous_step' | translate"
                        (handleClick)="checkoutFlowService.prevStep()"
                    ></app-button>
                    <app-button
                        buttonClass="button--primary button--has-spacing button--has-bottom-spacing"
                        buttonType="submit"
                        [loading]="!!(checkoutService.isCheckoutLoading$ | async)"
                        [title]="'buttons.continue' | translate"
                    >
                        <i aria-hidden="true" class="button__icon">${arrowRightIcon}</i>
                    </app-button>
                </div>
            </form>
        </div>
    `,
})
export class CheckoutStepDeliveryTabDeliveryComponent implements OnInit, OnDestroy {
    form?: UntypedFormGroup;
    timeSlot?: string;
    timeSlotInvalid?: boolean;
    private readonly unsubscribe = new Subject<void>();

    constructor(
        private fb: UntypedFormBuilder,
        public checkoutService: CheckoutService,
        public checkoutFlowService: CheckoutFlowService,
        private cd: ChangeDetectorRef
    ) {}
    async ngOnInit() {
        const { delivery } = await firstValueFrom(this.checkoutService.getCheckoutData());

        const deliveryData = delivery;
        let date: Date | undefined;

        if (deliveryData) {
            const { dateTime } = deliveryData;
            if (dateTime) {
                date = new Date(dateTime);
                this.timeSlot = getTimeFromUTCDate(date);
            }
        }

        this.form = this.fb.group(
            {
                date: [(date && getDateObjFromDate(date)) || '', Validators.required],
                address: [deliveryData?.address?.address || '', Validators.required],
                zipCode: [deliveryData?.address?.zipCode || '', [Validators.required, Validators.minLength(4), Validators.maxLength(4)]],
                city: [deliveryData?.address?.city || '', Validators.required],
            },
            {
                updateOn: 'blur',
            }
        );
        this.cd.markForCheck();
    }

    handleSlotClick(slot: string) {
        this.timeSlot = slot;
        this.timeSlotInvalid = false;
    }

    onSubmit() {
        if (!this.timeSlot) {
            this.timeSlotInvalid = true;
        }

        if (this.form?.invalid || this.form?.disabled || (this.form && this.timeSlotInvalid)) {
            FormService.markControlsAsTouched(this.form);
            return;
        }

        const { date, address, zipCode, city }: { [key: string]: string } = this.form?.value;

        const { year, month, day } = date as unknown as { year: number; month: number; day: number };

        const dateTime = new Date(getUTCDateString({ year, month, day, time: this.timeSlot })).toISOString();

        const reqBody: RequestsUpdatePickupDeliveryRequestDto = {
            deliveryType: DELIVERYTYPES.Delivery,
            delivery: {
                dateTime,
                address: {
                    address,
                    zipCode,
                    city,
                },
            },
        };

        this.checkoutService.setPickupDelivery(reqBody);
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
